import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1515263487990-61b07816b324?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 1rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const SectionSubtitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.8rem;
  color: #0d9488;
  margin-bottom: 1rem;
  font-style: italic;
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const ServiceSection = styled.div`
  margin-bottom: 6rem;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const PopUp: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const buttonVariants = {
    hover: { 
      scale: 1.05,
      backgroundColor: '#0f766e',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
      y: -5
    },
    tap: { scale: 0.95 }
  };
  
  // Services data
  const services = [
    {
      title: language === 'it' ? 'POP UP STORE' : 'POP UP STORE',
      description: language === 'it' 
        ? 'Trasforma la tua proprietà in un negozio temporaneo, un\'opportunità unica per i brand di testare nuovi mercati o presentare collezioni esclusive in location di prestigio.'
        : 'Transform your property into a temporary store, a unique opportunity for brands to test new markets or present exclusive collections in prestigious locations.',
      path: '/services/pop-up-store'
    },
    {
      title: language === 'it' ? 'SMART WORKING' : 'SMART WORKING',
      description: language === 'it' 
        ? 'Offri la tua proprietà come spazio di lavoro remoto, ideale per professionisti e team aziendali alla ricerca di ambienti ispiratori e funzionali dove lavorare in tranquillità.'
        : 'Offer your property as a remote workspace, ideal for professionals and business teams looking for inspiring and functional environments where they can work in peace.',
      path: '/services/smart-working'
    },
    {
      title: language === 'it' ? 'SHOOTING' : 'SHOOTING',
      description: language === 'it' 
        ? 'Metti a disposizione la tua proprietà per servizi fotografici, video promozionali o produzioni cinematografiche, sfruttando al massimo le caratteristiche uniche dei tuoi spazi.'
        : 'Make your property available for photoshoots, promotional videos or film productions, making the most of the unique features of your spaces.',
      path: '/services/shooting'
    },
    {
      title: language === 'it' ? 'FORMAZIONE' : 'TRAINING',
      description: language === 'it' 
        ? 'Partecipa ai nostri programmi di formazione per proprietari di immobili, gestori e professionisti del settore degli affitti brevi, per acquisire competenze specializzate e strategie vincenti.'
        : 'Participate in our training programs for property owners, managers and professionals in the short-term rental sector, to acquire specialized skills and winning strategies.',
      path: '/services/training'
    },
    {
      title: language === 'it' ? 'CORSI' : 'COURSES',
      description: language === 'it' 
        ? 'Scopri i nostri corsi specializzati su vari aspetti della gestione degli affitti brevi, dal marketing all\'ottimizzazione dei ricavi, fino alla gestione operativa quotidiana.'
        : 'Discover our specialized courses on various aspects of short-term rental management, from marketing to revenue optimization, to daily operational management.',
      path: '/services/courses'
    },
    {
      title: language === 'it' ? 'LEZIONI PRIVATE' : 'PRIVATE LESSONS',
      description: language === 'it' 
        ? 'Richiedi sessioni di coaching individuali con i nostri esperti per un approccio personalizzato alle sfide specifiche della tua attività di affitti brevi.'
        : 'Request individual coaching sessions with our experts for a personalized approach to the specific challenges of your short-term rental business.',
      path: '/services/private-lessons'
    },
    {
      title: language === 'it' ? 'EVENTI' : 'EVENTS',
      description: language === 'it' 
        ? 'Partecipa ai nostri eventi formativi e di networking, opportunità preziose per connettersi con altri professionisti del settore e scoprire le ultime tendenze del mercato.'
        : 'Participate in our training and networking events, valuable opportunities to connect with other industry professionals and discover the latest market trends.',
      path: '/services/training-events'
    },
    {
      title: language === 'it' ? 'SICUREZZA' : 'SECURITY',
      description: language === 'it' 
        ? 'Soluzioni complete per la sicurezza della tua proprietà e dei tuoi ospiti, dalla manutenzione preventiva all\'installazione di sistemi di sicurezza all\'avanguardia.'
        : 'Complete solutions for the safety of your property and your guests, from preventive maintenance to the installation of cutting-edge security systems.',
      path: '/services/security'
    },
    {
      title: language === 'it' ? 'ASSICURAZIONE' : 'INSURANCE',
      description: language === 'it' 
        ? 'Pacchetti assicurativi su misura per proteggere il tuo investimento immobiliare e la tua attività di affitti brevi da danni, responsabilità e perdite di reddito.'
        : 'Tailor-made insurance packages to protect your real estate investment and your short-term rental business from damage, liability and loss of income.',
      path: '/services/insurance'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Pop Up - Smart Working - Shooting | J-O-H' : 'Pop Up - Smart Working - Shooting | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Soluzioni innovative per immobili in affitti brevi: pop-up store, spazi per smart working e location per shooting fotografici e video' 
            : 'Innovative solutions for short-term rental properties: pop-up stores, smart working spaces and locations for photo and video shoots'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/property-owner">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>POP UP - SMART WORKING - SHOOTING</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <ServiceSection>
            <SectionTitle>{language === 'it' ? 'POP-UP STORE NEGLI IMMOBILI PER AFFITTI BREVI' : 'POP-UP STORE IN PROPERTIES FOR SHORT-TERM RENTALS'}</SectionTitle>
            <Description>
              {language === 'it' 
                ? 'Gli immobili per affitti brevi offrono la flessibilità di trasformarsi in pop-up store, che siano appartamenti, locali commerciali o uffici. Questa opzione consente di sfruttare lo spazio in modo dinamico e versatile, adattandolo rapidamente alle esigenze di esposizioni temporanee, vendite speciali o eventi promozionali.'
                : 'Properties for short-term rentals offer the flexibility to transform into pop-up stores, whether apartments, commercial spaces or offices. This option allows you to use the space in a dynamic and versatile way, quickly adapting it to the needs of temporary exhibitions, special sales or promotional events.'}
            </Description>
          </ServiceSection>
          
          <ServiceSection>
            <SectionTitle>{language === 'it' ? 'SMART WORKING NEGLI IMMOBILI PER AFFITTI BREVI' : 'SMART WORKING IN PROPERTIES FOR SHORT-TERM RENTALS'}</SectionTitle>
            <Description>
              {language === 'it' 
                ? 'Gli immobili per affitti brevi sono ideali anche per lo smart working, offrendo ambienti confortevoli e attrezzati per lavorare in remoto. Dotati di connessione internet ad alta velocità, spazi ergonomici e servizi aggiuntivi, questi spazi consentono di conciliare la vita lavorativa con il comfort e la flessibilità di lavorare ovunque ci si trovi.'
                : 'Short-term rental properties are also ideal for smart working, offering comfortable and equipped environments for remote working. Equipped with high-speed internet connection, ergonomic spaces and additional services, these spaces allow you to reconcile work life with the comfort and flexibility of working wherever you are.'}
            </Description>
          </ServiceSection>
          
          <ServiceSection>
            <SectionTitle>{language === 'it' ? 'SHOOTING NEGLI IMMOBILI PER AFFITTI BREVI' : 'SHOOTING IN PROPERTIES FOR SHORT-TERM RENTALS'}</SectionTitle>
            <Description>
              {language === 'it' 
                ? 'Gli immobili per affitti brevi offrono una piattaforma versatile per lo svolgimento di shooting per case di moda, per privati e per spot pubblicitari. Con la loro varietà di ambienti e stili, consentono di creare atmosfere diverse e coinvolgenti che si adattano perfettamente alle esigenze di ogni produzione. Questi spazi offrono una soluzione ideale per realizzare scatti fotografici e video di alta qualità.'
                : 'Short-term rental properties offer a versatile platform for conducting shoots for fashion houses, private individuals and commercials. With their variety of environments and styles, they allow you to create different and engaging atmospheres that adapt perfectly to the needs of each production. These spaces offer an ideal solution for creating high-quality photographs and videos.'}
            </Description>
          </ServiceSection>
          
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <Button variant="outline" to={service.path}>
                  {language === 'it' ? 'Scopri' : 'Discover'}
                </Button>
              </ServiceCard>
            ))}
          </ServicesGrid>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default PopUp; 